/**
 * "Use Now" / "Usar Agora" CTA — GA4 dataLayer custom event for pipe-clone button clicks.
 *
 * These clicks were not tracked at all before 2026-08-13 (product card button, sticky mobile
 * CTA). A single delegated listener covers every current and future rendering surface — cards
 * render server-side across many templates (archive, sliders, related products) rather than
 * through one JS-owned component, so binding per-button on init would miss any added later.
 *
 * Buttons opt in via `data-track="use_now_cta_click"` plus `data-pipe-id`/`data-lang`
 * (see PipeCloneButton component and fixed-bottom-buttons.blade.php).
 *
 * @see resources/js/book-a-demo-tracking.js for the sibling pattern this mirrors.
 */
import { PipefyLogger } from './utils/logger.js';

const logger = PipefyLogger.create('UseNowTracking');

const TRACK_SELECTOR = '[data-track="use_now_cta_click"]';

function hasConsent() {
  const consent = Boolean(
    typeof window !== 'undefined' && window.PipefyTracking?.hasMarketingConsent?.()
  );
  if (!consent) {
    logger.warn('consent: marketing consent not granted — tracking suppressed');
  }
  return consent;
}

function currentPageUrl() {
  return typeof window !== 'undefined' && window.location
    ? `${window.location.origin}${window.location.pathname}${window.location.search}`
    : '';
}

/**
 * @param {{ pipeId?: string, lang?: string }} [opts]
 */
export function trackUseNowCtaClick(opts = {}) {
  logger.info('ctaClick: tracking', opts);
  if (!hasConsent() || typeof window === 'undefined') return;

  const payload = {
    event: 'cta_click',
    event_name: 'use_now_cta_click',
    page_url: currentPageUrl(),
    pipe_id: String(opts.pipeId || ''),
    lang: String(opts.lang || ''),
  };
  logger.debug('dataLayer: push', payload);
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

/**
 * Bind the delegated click listener once. Safe to call multiple times.
 */
export function initUseNowCtaTracking() {
  if (typeof document === 'undefined') return;
  if (document.body?.dataset.useNowTrackingBound === 'true') return;
  if (document.body) document.body.dataset.useNowTrackingBound = 'true';

  document.addEventListener('click', event => {
    const el = event.target instanceof Element ? event.target.closest(TRACK_SELECTOR) : null;
    if (!el) return;

    trackUseNowCtaClick({
      pipeId: el.getAttribute('data-pipe-id') || '',
      lang: el.getAttribute('data-lang') || '',
    });
  });
}
